// -- Toggle ---------------------------------------------------
// Toggles aria-pressed on .toggle buttons, plus the named-state API so
// agents/tests can drive the pressed state by name (AGENTS.md "State API").
// Skips toggles inside .toggle-group - those are managed by toggle-group.js.

// Shared preamble (AGENTS.md "State API"); the implementation lives in core.js —
// build.ts rewrites this import into a df$.shadcn.shared binding in dist/.
import { defussGlobals } from '../../shared/state-api.js';

const df$ = defussGlobals();

const toggleStates = ['default', 'pressed'];

/**
 * UI side of setState: 'pressed' sets aria-pressed=true, 'default' restores
 * the authored aria-pressed value (snapshotted at init per instance).
 */
function triggerStateChange(toggle, stateName, _config) {
  switch (stateName) {
    case 'default':
      toggle.setAttribute('aria-pressed', toggle._defaultPressed ?? 'false');
      break;
    case 'pressed':
      toggle.setAttribute('aria-pressed', 'true');
      break;
  }
}

/** Registry-level API; pass the toggle button explicitly. Unknown names throw. */
export const toggleApi = {
  setState(toggle, stateName, config = {}) {
    if (!toggleStates.includes(stateName)) {
      throw new Error(`toggle: unknown state "${stateName}" (supported: ${toggleStates.join(', ')})`);
    }
    triggerStateChange(toggle, stateName, config);
    // state lives on the ELEMENT, not the module (many toggles per page)
    toggle.dataset.stateName = stateName;
    toggle._stateConfig = config;
  },
  getState(toggle) {
    // reflect reality: user clicks change aria-pressed without setState()
    const pressed = toggle.getAttribute('aria-pressed') === 'true';
    return {
      name: toggle.dataset.stateName || (pressed ? 'pressed' : 'default'),
      config: toggle._stateConfig ?? {},
    };
  },
};

df$.toggleApi = toggleApi;
df$.toggleStates = toggleStates;

function init() {
  document.querySelectorAll('.toggle:not([data-init]):not(.toggle-group .toggle)').forEach((toggle) => {
  toggle.dataset.init = '';
  // remember the authored pressed state so setState('default') can restore it
  toggle._defaultPressed = toggle.getAttribute('aria-pressed') || 'false';
  // bind-scope the api per instance: `$('#my-toggle').api.setState('pressed')`
  toggle.api = {
    setState: (stateName, config) => toggleApi.setState(toggle, stateName, config),
    getState: () => toggleApi.getState(toggle),
  };
  toggle.addEventListener('click', () => {
    const pressed = toggle.getAttribute('aria-pressed') === 'true';
    toggle.setAttribute('aria-pressed', !pressed);
    // user interaction also moves the named state (keeps getState honest)
    toggle.dataset.stateName = !pressed ? 'pressed' : 'default';
  });
});
}

init();
new MutationObserver(init).observe(document, { childList: true, subtree: true });
